"use client";

type Colour = "bumble" | "bff" | "badoo";
import { Button, Header2, Header3, P1 } from "@bumble/ui-kit";
import styles from "./TabbedSection.module.css";
import MaxWidth from "@/components/MaxWidth";
import { useState } from "react";
import { isExternalUrl } from "@/lib/utils/isExternalUrl";
interface TabbedSectionProps {
  data?: {
    title?: string;
    copy?: string;
    tabs?: Array<{
      id?: string;
      title?: string;
      copy?: string;
      colour?: string;
      image?: {
        url?: string;
      };
      logo?: {
        url?: string;
      };
      cta?: {
        text?: string;
        link?: string;
      };
    }>;
  };
}
export default function TabbedSection({
  data
}: TabbedSectionProps) {
  const [activeTab, setActiveTab] = useState<number>(0);
  const handleTabClick = (tab: number) => {
    setActiveTab(tab);
  };
  const tabs = data?.tabs?.map(tab => ({
    title: tab?.title ?? "",
    copy: tab?.copy ?? "",
    image: tab?.image?.url ?? "/assets/placeholder-background.png",
    cta: tab?.cta ? {
      text: tab.cta.text ?? "",
      href: tab.cta.link ?? "#"
    } : undefined,
    colour: normaliseColour(tab?.colour),
    logo: tab?.logo?.url ?? "/assets/placeholder-background.png"
  })) ?? [];
  if (tabs.length === 0) {
    return null;
  }
  return <MaxWidth>
      <div className={styles.tabbed__section}>
        <div className={styles.copy}>
          <Header2>{data?.title ?? "Our Brands"}</Header2>
          <P1>{data?.copy ?? ""}</P1>
        </div>

        <div className={styles.tab__navigation}>
          {tabs.map((item, index) => {
          return <TabButton key={index} title={item.title} active={activeTab === index} onClick={() => handleTabClick(index)} colour={item.colour as Colour} logo={item.logo} />;
        })}
        </div>

        <div className={styles.tabs}>
          {tabs.map((item, index) => {
          return <Tab key={index} title={item.title} copy={item.copy} image={item.image} cta={item.cta} colour={item.colour as Colour} active={activeTab === index} logo={item.logo} onClick={() => handleTabClick(index)} />;
        })}
        </div>
      </div>
    </MaxWidth>;
}
function normaliseColour(colour?: string): Colour {
  if (colour === "bff" || colour === "badoo" || colour === "bumble") {
    return colour;
  }
  return "bumble";
}
const colourHex: Record<Colour, string> = {
  bumble: "#ffdb5b",
  bff: "#fff28e",
  badoo: "#e9d8ff"
};
interface TabProps {
  title: string;
  copy: string;
  image?: string;
  cta?: {
    text: string;
    href: string;
  };
  colour: Colour;
  active: boolean;
  logo?: string;
  onClick?: () => void;
}
function Tab({
  title,
  copy,
  image = "/assets/placeholder-background.png",
  cta,
  colour,
  active,
  logo = "/assets/placeholder-background.png",
  onClick
}: TabProps) {
  const isCtaExternal = isExternalUrl(cta?.href);
  return <div className={`${styles.tab} ${active ? styles.active : ""} ${styles[`colour__${colour}`]}`} onClick={!active ? onClick : undefined} style={{
    color: colourHex[colour]
  }}>
      <div className={styles.mobile__logo}>
        <img src={logo} alt={title} loading="lazy" />
      </div>

      <div className={styles.tab__left}>
        <P1 as="h3">{title}</P1>
        <Header3 as="p">{copy}</Header3>
        <Button text={cta?.text} href={cta?.href} extraClass={styles.tab__cta} isTargetBlank={isCtaExternal} />
      </div>

      <div className={styles.tab__right}>
        <img src={image} alt={title} loading="lazy" />
      </div>
    </div>;
}
interface TabButtonProps {
  title: string;
  active: boolean;
  onClick: () => void;
  colour: Colour;
  logo?: string;
}
function TabButton({
  title,
  active,
  onClick,
  colour,
  logo = "/assets/placeholder-background.png"
}: TabButtonProps) {
  return <button className={`${styles.tab__button} ${active ? styles.active : ""} ${styles[`colour__${colour}`]}`} onClick={onClick}>
      <img alt={title} src={logo} />
    </button>;
}